// Utilidad central para interpretar las "observaciones" de los envíos.
//
// Las observaciones guardan, mezclados, dos tipos de información:
//   1) Marcadores internos que usa el sistema (ej: "SOLICITUD ENVÍO ADELANTADO",
//      "[DESGLOSE] 3 del mes + 1 pendiente = 4 total", "📅 DÍA DE ENVÍO: Lunes").
//   2) El texto real que escribió la persona (la descripción del envío).
//
// Mostrar (1) crudo al usuario confunde. Esta función separa ambas cosas para que
// la interfaz pueda mostrar etiquetas/badges limpios y, aparte, solo el texto humano.

/**
 * Analiza una cadena de observaciones y devuelve sus partes interpretadas.
 * @param {string|null|undefined} observacionesRaw
 * @returns {{
 *   tipo: 'adelantado' | 'pendientes' | 'normal',
 *   diaEnvio: string | null,
 *   desglose: { delMes: number, pendientes: number, total: number } | null,
 *   guia: string | null,
 *   textoUsuario: string
 * }}
 */
export function parsearObservaciones(observacionesRaw) {
  const texto = (observacionesRaw || '').toString();

  // --- Detectar tipo de envío ---
  let tipo = 'normal';
  if (texto.includes('SOLICITUD ENVÍO ADELANTADO') || texto.includes('ENVÍO POR ADELANTADO')) {
    tipo = 'adelantado';
  } else if (texto.includes('SOLICITUD DESPACHO PENDIENTES')) {
    tipo = 'pendientes';
  }

  // --- Día de envío (ej: "📅 DÍA DE ENVÍO: Lunes") ---
  const diaMatch = texto.match(/📅?\s*DÍA DE ENVÍO:\s*([A-Za-zÁÉÍÓÚáéíóúñÑ]+)/);
  const diaEnvio = diaMatch ? diaMatch[1] : null;

  // --- Desglose (ej: "[DESGLOSE] 3 del mes + 1 pendiente(s) anterior(es) = 4 total") ---
  let desglose = null;
  const desgloseMatch = texto.match(/\[DESGLOSE\]\s*(\d+)\s*del mes\s*\+\s*(\d+)\s*pendiente[^=]*=\s*(\d+)\s*total/i);
  if (desgloseMatch) {
    desglose = {
      delMes: parseInt(desgloseMatch[1], 10),
      pendientes: parseInt(desgloseMatch[2], 10),
      total: parseInt(desgloseMatch[3], 10),
    };
  }

  // --- Número de guía embebido (ej: "GUÍA: 12345") ---
  const guiaMatch = texto.match(/GU[ÍI]A:\s*([^\s|]+)/i);
  const guia = guiaMatch ? guiaMatch[1] : null;

  // --- Texto limpio: quitar todos los marcadores internos ---
  let textoUsuario = texto;

  // Quitar el bloque de solicitud adelantada con su prefijo completo
  // "SOLICITUD ENVÍO ADELANTADO - Cliente: X - Tóner: Y - Mes: Z" (y un posible " - " final antes del texto real)
  textoUsuario = textoUsuario.replace(
    /SOLICITUD ENVÍO ADELANTADO\s*-\s*Cliente:[^-]*-\s*Tóner:[^-]*-\s*Mes:[^-]*(-\s*)?/i,
    ''
  );
  // Solicitud de despacho de pendientes con su detalle
  textoUsuario = textoUsuario.replace(
    /SOLICITUD DESPACHO PENDIENTES\s*-\s*Mes original:[^-]*-\s*Cantidad pendiente total:\s*\d+\s*/i,
    ''
  );
  // Marcadores sueltos restantes
  textoUsuario = textoUsuario.replace(/SOLICITUD ENVÍO ADELANTADO/gi, '');
  textoUsuario = textoUsuario.replace(/SOLICITUD DESPACHO PENDIENTES/gi, '');
  textoUsuario = textoUsuario.replace(/ENVÍO POR ADELANTADO/gi, '');
  // Bloque de desglose
  textoUsuario = textoUsuario.replace(/\[DESGLOSE\][^|]*/i, '');
  // Día de envío
  textoUsuario = textoUsuario.replace(/📅?\s*DÍA DE ENVÍO:\s*[A-Za-zÁÉÍÓÚáéíóúñÑ]+/i, '');
  // Guía embebida
  textoUsuario = textoUsuario.replace(/GU[ÍI]A:\s*[^\s|]+/i, '');

  // Limpiar separadores "|" y guiones sobrantes y espacios dobles
  textoUsuario = textoUsuario
    .replace(/\s*\|\s*/g, ' ')
    .replace(/^\s*-\s*/, '')
    .replace(/\s*-\s*$/, '')
    .replace(/\s{2,}/g, ' ')
    .trim();

  return { tipo, diaEnvio, desglose, guia, textoUsuario };
}

/**
 * Devuelve una etiqueta legible para el tipo de envío.
 */
export function etiquetaTipoEnvio(tipo) {
  switch (tipo) {
    case 'adelantado':
      return { texto: 'Envío Adelantado', icono: '🚀', clases: 'bg-purple-100 text-purple-700 border border-purple-200' };
    case 'pendientes':
      return { texto: 'Despacho de Pendientes', icono: '🚚', clases: 'bg-orange-100 text-orange-700 border border-orange-200' };
    default:
      return { texto: 'Envío Normal', icono: '📊', clases: 'bg-blue-100 text-blue-700 border border-blue-200' };
  }
}